
import React from 'react';
import { Helmet } from 'react-helmet'
import { StaticImage } from "gatsby-plugin-image"
import Header from '../components/Header2';
import Footer from '../components/Footer';
import SectionWrapper from '../components/SectionWrapper';
import ServiceCard from '../components/ServiceCard';
import InfoCard from '../components/InfoCard';
import Accordion from '../components/Accordion';
import { 
  SERVICES_DATA, 
  PARENT_INFO_ITEMS, 
  DENTAL_TIPS, 
  FAQ_DATA,
  CalendarIcon,
  PhoneIcon,
  InformationCircleIcon
} from '../constants';
import { Service, InfoItem } from '../types';
import { graphql } from 'gatsby';


const App: React.FC = () => {
  return (    
    <div className="flex flex-col min-h-screen bg-slate-50">
      <Helmet title="Dentobus NFZ Lubuskie" defer={false} />
      <Header />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-sky-500 to-cyan-400 text-white pt-20 pb-28 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
             {/* Decorative background pattern or image can go here */}
          </div>
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold mb-6 drop-shadow-md">
              Twój Mobilny Gabinet Stomatologiczny!
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl mb-10 max-w-3xl mx-auto drop-shadow-sm">
              Bezpłatna opieka stomatologiczna dla dzieci i młodzieży do 18. roku życia.
              Dbamy o zdrowy uśmiech przyszłości!
            </p>
            <a
              href="#schedule"
              className="bg-yellow-400 hover:bg-yellow-500 text-sky-800 font-bold py-3 px-8 rounded-lg text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              Sprawdź Harmonogram
            </a>
          </div>
          <div className="absolute bottom-0 left-0 w-full h-20 bg-slate-50" style={{ clipPath: 'polygon(0 100%, 100% 0, 100% 100%)' }}></div>
        </section>

        {/* About Section */}
        <SectionWrapper id="about" title="O Dentobusie NFZ" subtitle="Dowiedz się więcej o naszej misji i dla kogo jesteśmy.">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <StaticImage src="../assets/images/dentobus2.jpg" alt="Dentobus - mobilny gabinet" className="rounded-lg shadow-xl w-full h-auto object-cover" />
            </div>
            <div className="text-slate-700 space-y-4">
              <p className="text-lg">
                Dentobus NFZ to specjalistyczny, mobilny gabinet stomatologiczny, który dociera do szkół i miejscowości, 
                gdzie dostęp do opieki dentystycznej może być utrudniony.
              </p>
              <p>
                Naszą misją jest zapewnienie kompleksowej profilaktyki i podstawowego leczenia stomatologicznego dzieciom i młodzieży, 
                promując zdrowy uśmiech od najmłodszych lat. Program finansowany jest przez Narodowy Fundusz Zdrowia.
              </p>
              <p>
                Usługi Dentobusu są skierowane do wszystkich dzieci i młodzieży do ukończenia 18. roku życia, objętych ubezpieczeniem zdrowotnym.
              </p>
            </div>
          </div>
        </SectionWrapper>

        {/* Services Section */}
        <SectionWrapper id="services" title="Nasze Usługi" subtitle="Oferujemy szeroki zakres bezpłatnych świadczeń stomatologicznych." className="bg-sky-50">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {SERVICES_DATA.map((service: Service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>
        </SectionWrapper>

        {/* Schedule Section */}
        <SectionWrapper id="schedule" title="Harmonogram Postojów" subtitle="Sprawdź, kiedy Dentobus odwiedzi Twoją okolicę.">
          <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-2xl mx-auto">
            <CalendarIcon className="h-16 w-16 text-sky-500 mx-auto mb-6" />
            <h3 className="text-2xl font-semibold text-slate-700 mb-3">Aktualny Harmonogram</h3>
            <p className="text-slate-600 mb-6">
              Informacje o najbliższych postojach Dentobusu są regularnie aktualizowane. 
              Prosimy o sprawdzanie harmonogramu w lokalnej szkole, urzędzie gminy lub na stronie internetowej Twojego oddziału NFZ.
            </p>
            <a 
              href="https://pacjent.gov.pl/zapobiegaj/leczenie-w-dentobusie" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block bg-sky-500 hover:bg-sky-600 text-white font-semibold py-3 px-6 rounded-lg shadow hover:shadow-md transition-all duration-300"
            >
              Sprawdź miejsca postoju dentobusu
            </a>
          </div>
        </SectionWrapper>

        {/* For Parents Section */}
        <SectionWrapper id="parents" title="Informacje dla Rodziców" subtitle="Wszystko, co musisz wiedzieć przed wizytą dziecka w Dentobusie." className="bg-green-50">
          <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 mb-12">
            {PARENT_INFO_ITEMS.map((item: InfoItem) => (
              <InfoCard key={item.id} item={item} />
            ))}
          </div>
          <h3 className="text-2xl font-semibold text-slate-700 mb-6 text-center">Najczęściej Zadawane Pytania (FAQ)</h3>
          <Accordion items={FAQ_DATA} />
        </SectionWrapper>

        {/* Tips for Kids Section */}
        <SectionWrapper id="tips" title="Porady dla Małych Pacjentów" subtitle="Jak dbać o ząbki, by były zdrowe i piękne!">
           <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            {DENTAL_TIPS.map((item: InfoItem) => (
              <InfoCard key={item.id} item={item} />
            ))}
          </div>
        </SectionWrapper>

        {/* Contact Section */}
        <SectionWrapper id="contact" title="Kontakt" subtitle="Masz pytania? Skontaktuj się z nami." className="bg-slate-100">
          <div className="max-w-xl mx-auto bg-white p-8 rounded-xl shadow-lg text-center">
            <InformationCircleIcon className="h-16 w-16 text-sky-500 mx-auto mb-6" />
            <p className="text-slate-700 mb-4 text-lg">
              W celu uzyskania szczegółowych informacji o programie Dentobus, harmonogramie wizyt oraz wymaganych dokumentach, prosimy kontaktować się z:
            </p>
            <ul className="text-slate-600 space-y-3">
              <li className="flex items-center justify-center">
                <PhoneIcon className="h-5 w-5 text-sky-500 mr-2" />
                <span>Koordynator: <strong>880 915 204</strong></span>
              </li>
               <li className="flex items-center justify-center">
                <span>Email: <strong>biuro@dentobus-lubuskie.pl</strong></span>
              </li>
              <li>
                Dyrekcją szkoły lub placówki oświatowej Twojego dziecka.
              </li>
            </ul>
            <p className="mt-6 text-sm text-slate-500">
              Pamiętaj, że Dentobus to inicjatywa NFZ mająca na celu poprawę dostępności opieki stomatologicznej dla dzieci i młodzieży.
            </p>
          </div>
        </SectionWrapper>
      </main>
      <Footer />
    </div>
  );
};

export const query = graphql`
 query {
    dentobus: file(relativePath: {eq: "images/dentobus2.jpg"}) {
    childImageSharp {
      fluid(maxHeight: 92) {
        src
        sizes
        srcSet
        srcWebp
        base64
      }
    }
  }
  }
`

export default App;
